// File: exampleCar
// Created: 2026-05-29
// Updated: 2026-05-29
// Author: Erick Hernández Silva

import type { CarConfig, ViewImages } from './carWizardTypes';

// All six views marked Not-Apply (a valid, complete image set).
const NA: ViewImages = { view1: 'x', view2: 'x', view3: 'x', view4: 'x', view5: 'x', view6: 'x' };
// Intentionally missing view1 (one validation error to demonstrate the check).
const MISSING_V1: ViewImages = { view2: 'x', view3: 'x', view4: 'x', view5: 'x', view6: 'x' };

export const exampleCar: CarConfig = {
  id: '',
  createdAt: '',
  carName: 'Mercedes G-Wagon (example)',
  basePrice: 150000,
  baseRenderOptionId: 'P1',
  steps: [
    {
      stepId: 'engine',
      stepName: 'Engine',
      stepDescription: 'Powertrain (text only, not rendered)',
      isTextOnly: true,
      required: true,
      options: [
        { optionId: 'E1', optionName: '5.0L M113 V8', optionDescription: '~300 hp', optionPrice: 25000, images: {}, subOptions: [], isDefault: true },
        { optionId: 'E2', optionName: '5.4L M113 V8', optionDescription: '~350 hp', optionPrice: 33500, images: {}, subOptions: [] },
      ],
    },
    {
      stepId: 'gearbox',
      stepName: 'Gearbox',
      stepDescription: 'Transmission (text only)',
      isTextOnly: true,
      required: true,
      options: [
        { optionId: 'G1', optionName: 'Automatic', optionPrice: 0, images: {}, subOptions: [], isDefault: true },
        { optionId: 'G2', optionName: 'Manual', optionPrice: 0, images: {}, subOptions: [] },
      ],
    },
    {
      stepId: 'exterior-paint',
      stepName: 'Exterior paint',
      stepDescription: 'Body color (base render)',
      isTextOnly: false,
      required: true,
      options: [
        { optionId: 'P1', optionName: 'Gloss black', optionPrice: 0, images: { ...NA }, subOptions: [] },
        { optionId: 'P2', optionName: 'Gloss dark green', optionPrice: 4000, images: { ...NA }, subOptions: [] },
        // Intentionally incomplete → 1 validation error.
        { optionId: 'P3', optionName: 'Matte olive', optionPrice: 6000, images: { ...MISSING_V1 }, subOptions: [] },
      ],
    },
    {
      stepId: 'side-steps',
      stepName: 'Side steps',
      stepDescription: 'Step type',
      isTextOnly: false,
      options: [
        { optionId: 'SS1', optionName: 'Static loop', optionPrice: 0, images: {}, subOptions: [], includedInBaseRender: true },
        { optionId: 'SS2', optionName: 'Automatic electric', optionPrice: 8000, images: { ...NA }, subOptions: [] },
      ],
    },
    {
      stepId: 'exhaust-pipes',
      stepName: 'Exhaust pipes',
      stepDescription: 'Exhaust outlet',
      isTextOnly: false,
      options: [
        { optionId: 'EP1', optionName: 'Double oval', optionPrice: 0, images: { ...NA }, subOptions: [] },
        { optionId: 'EP2', optionName: 'Hidden', optionPrice: 0, images: {}, includedInBaseRender: true, subOptions: [] },
      ],
    },
    {
      stepId: 'interior-color',
      stepName: 'Interior color',
      stepDescription: 'Leather color',
      isTextOnly: false,
      required: true,
      options: [
        { optionId: 'X1', optionName: 'Off white', optionPrice: 0, images: { ...NA }, subOptions: [], isDefault: true },
        { optionId: 'X2', optionName: 'Cognac', optionPrice: 2500, images: { ...NA }, subOptions: [] },
        { optionId: 'X3', optionName: 'Black', optionPrice: 2500, images: { ...NA }, subOptions: [] },
      ],
    },
    {
      stepId: 'seating-setup',
      stepName: 'Seating setup',
      stepDescription: 'Seat layout (color follows interior)',
      isTextOnly: false,
      options: [
        {
          optionId: 'S1',
          optionName: '2+2 RECARO',
          optionPrice: 0,
          images: {},
          includedInBaseRender: true,
          // Coupled: each variant maps to an interior-color option by id.
          subOptionsLinkedToStepId: 'interior-color',
          subOptions: [
            { optionId: 'S11', optionName: 'Off white', linkedOptionId: 'X1', optionPrice: 0, images: { ...NA } },
            { optionId: 'S12', optionName: 'Cognac', linkedOptionId: 'X2', optionPrice: 0, images: { ...NA } },
            { optionId: 'S13', optionName: 'Black', linkedOptionId: 'X3', optionPrice: 0, images: { ...NA } },
          ],
        },
      ],
    },
    {
      stepId: 'steering-wheel',
      stepName: 'Steering wheel',
      stepDescription: 'Wheel + variant',
      isTextOnly: false,
      options: [
        {
          optionId: 'O1',
          optionName: 'Original',
          optionPrice: 0,
          images: { ...NA },
          // Coupled to interior color, plus a fixed "Black" manual variant
          // (no linkedOptionId) so the wizard shows the matching/fixed selector.
          subOptionsLinkedToStepId: 'interior-color',
          subOptions: [
            { optionId: 'O11', optionName: 'Off white', linkedOptionId: 'X1', optionPrice: 0, images: { ...NA } },
            { optionId: 'O12', optionName: 'Cognac', linkedOptionId: 'X2', optionPrice: 0, images: { ...NA } },
            { optionId: 'O13', optionName: 'Black', linkedOptionId: 'X3', optionPrice: 0, images: { ...NA } },
            { optionId: 'O14', optionName: 'Black leather', optionPrice: 0, images: { ...NA } },
          ],
        },
        // Intentionally incomplete → 1 validation error.
        { optionId: 'O2', optionName: 'Nardi', optionPrice: 1500, images: { ...MISSING_V1 }, subOptions: [] },
      ],
    },
  ],
  screens: [
    { screenId: 'powertrain', screenName: 'Powertrain', order: 1, stepIds: ['engine', 'gearbox'] },
    { screenId: 'exterior', screenName: 'Exterior', order: 2, stepIds: ['exterior-paint', 'side-steps', 'exhaust-pipes'] },
    { screenId: 'interior', screenName: 'Interior', order: 3, stepIds: ['interior-color', 'seating-setup', 'steering-wheel'] },
  ],
  rules: [
    {
      ruleId: 'auto-electric-hides-exhaust',
      when: { selections: [{ stepId: 'side-steps', optionIdIn: ['SS2'] }] },
      then: [{ type: 'forceOption', targetStepId: 'exhaust-pipes', targetOptionId: 'EP2' }],
    },
    {
      ruleId: 'manual-hides-nardi',
      when: { selections: [{ stepId: 'gearbox', optionIdIn: ['G2'] }] },
      then: [{ type: 'hideOption', targetStepId: 'steering-wheel', targetOptionId: 'O2' }],
    },
  ],
};
